import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Globe, Instagram, MessageCircle, ShoppingBag } from "lucide-react";
import MarketingFooter from "../components/MarketingFooter";
import MarketingPageShell from "../components/MarketingPageShell";
import usePageMeta from "../hooks/usePageMeta";

const SURFACES = [
  {
    icon: Globe,
    title: "On your website",
    body: "Paste a one-line HTML snippet in your footer or checkout page. The badge live-verifies the policy when clicked.",
  },
  {
    icon: MessageCircle,
    title: "On your WhatsApp catalogue",
    body: "Save the static PNG and use it as your WhatsApp Business display picture, catalogue cover, or pinned status.",
  },
  {
    icon: Instagram,
    title: "On Instagram shop",
    body: "Add the badge to your IG bio link page (Linktree / Beacons) or shop banner so buyers see you're protected.",
  },
  {
    icon: ShoppingBag,
    title: "On a POS / checkout screen",
    body: "Display the badge on your till receipt printer or self-checkout screen for in-person customers.",
  },
];

export default function TrustBadgePage() {
  usePageMeta({
    title: "Fortiqa Verified Merchant — Trust Badge",
    description:
      "Verified Fortiqa merchants get a live-verifying Trust Badge to display on their website, WhatsApp catalogue, IG shop, or POS.",
  });
  return (
    <div className="min-h-screen bg-[#f4f2eb] text-[#1b1d1c]" data-testid="trust-badge-page">
      <MarketingPageShell
        eyebrow="Merchant Trust Badge"
        title="Show customers you've got their back."
        intro="Verified Fortiqa merchants can display the Trust Badge anywhere they trade. The badge live-verifies the policy on click — and auto-revokes if cover lapses, so it's always honest."
        testid="trust-badge"
      >
        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-14 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="rounded-3xl bg-white border border-[#114232]/10 p-7 sm:p-10 shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <img src="/fortiqa-mark.svg" alt="Fortiqa" className="w-14 h-14 object-contain" />
                <div>
                  <div className="font-heading font-bold text-xl text-[#114232]">
                    Fortiqa Verified
                  </div>
                  <div className="text-xs text-[#5c6360] font-mono">
                    fortiqa.co.ke/verify/MW-2026
                  </div>
                </div>
              </div>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#5c6360]">Merchant</span>
                  <span className="font-semibold text-[#114232]">Mama Wanjiru Boutique</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#5c6360]">Cover tier</span>
                  <span className="font-semibold text-[#114232]">Growth</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#5c6360]">Active since</span>
                  <span className="font-semibold text-[#114232]">Aug 2025</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#5c6360]">Status</span>
                  <span className="inline-flex items-center gap-1.5 font-semibold text-[#2d7a4a]">
                    <span className="w-2 h-2 rounded-full bg-[#2d7a4a] animate-pulse" /> Live
                  </span>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-[#114232]/10 text-xs text-[#5c6360] text-center">
                Click to verify this policy on fortiqa.co.ke
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <ul className="space-y-3.5">
              {[
                "Live-verified — auto-revokes if cover lapses",
                "One-line HTML embed snippet or static PNG",
                "Customer clicks → verify the policy in real time",
                "Free for every active Fortiqa merchant",
              ].map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm sm:text-base text-[#1b1d1c]">
                  <BadgeCheck className="w-5 h-5 text-[#2d7a4a] mt-0.5 shrink-0" strokeWidth={2} />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-14">
          <div className="font-heading text-xl sm:text-2xl font-bold text-[#114232] mb-5">
            Where you can display it
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SURFACES.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="rounded-2xl bg-white border border-[#114232]/10 p-6 hover:border-[#114232]/30 hover:shadow-md transition-all"
              >
                <div className="w-10 h-10 rounded-xl bg-[#114232]/8 grid place-items-center mb-4">
                  <Icon className="w-5 h-5 text-[#114232]" strokeWidth={1.7} />
                </div>
                <div className="font-heading text-base font-bold text-[#114232] mb-1.5">{title}</div>
                <p className="text-sm text-[#5c6360] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-24">
          <div className="bg-[#114232] rounded-3xl p-8 sm:p-10 lg:p-14 text-[#f4f2eb] flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#d49a36] mb-2">
                Ready
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold leading-tight">
                Apply for your Trust Badge.
              </h3>
              <p className="text-[#f4f2eb]/80 mt-2 max-w-md text-sm sm:text-base">
                Free for every active merchant — issued the moment your cover goes live.
              </p>
            </div>
            <Link
              to="/signup"
              data-testid="trust-badge-cta-apply"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#c15337] hover:bg-[#a8472f] text-white font-semibold transition-all shadow-md hover:shadow-lg"
            >
              Apply for badge <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </MarketingPageShell>
      <MarketingFooter />
    </div>
  );
}
